import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Input,
  List,
  Typography,
  Button,
  Space,
  Card,
  Image,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { MessageTemplateData } from "../../models/messageTemplates";
import {
  renderInlineBoldPreview,
  renderMessage,
} from "../../utils/renderMessage";
import { CampaignTypeEnum } from "../../constants/enums";

const { Text, Title } = Typography;

interface Props {
  open: boolean;
  templates: MessageTemplateData[];
  selectedTemplates?: MessageTemplateData;
  campaignType?: CampaignTypeEnum;
  onCancel: () => void;
  onSelect: (template: MessageTemplateData) => void;
}

const MessageTemplatesModal: React.FC<Props> = ({
  open,
  templates,
  selectedTemplates,
  campaignType,
  onCancel,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MessageTemplateData | null>(null);

  const filteredTemplates = useMemo(() => {
    return templates
      .filter((t) => t.Name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const aMatch = a.CampaignType === campaignType ? 1 : 0;
        const bMatch = b.CampaignType === campaignType ? 1 : 0;
        return bMatch - aMatch;
      });
  }, [templates, search, campaignType]);

  useEffect(() => {
    setSelected(null);
  }, [campaignType]);

  useEffect(() => {
    if (selectedTemplates) {
      setSelected(selectedTemplates);
    }
  }, [selectedTemplates]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1000}
      title="Select WhatsApp Template"
      destroyOnClose
    >
      <div style={{ display: "flex", gap: 16 }}>
        {/* LEFT PANEL */}
        <div style={{ width: "45%" }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 12 }}
          />

          <List
            dataSource={filteredTemplates}
            rowKey="Id"
            renderItem={(item) => {
              const isActive = selected?.Id === item.Id;
              const isDisabled =
                campaignType !== undefined &&
                item.CampaignType !== campaignType;

              return (
                <List.Item
                  onClick={() => {
                    if (!isDisabled) {
                      setSelected(item);
                    }
                  }}
                  style={{
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.5 : 1,
                    border: isActive
                      ? "1px solid #1677ff"
                      : "1px solid #f0f0f0",
                    borderRadius: 8,
                    marginBottom: 8,
                    padding: 5,
                    background: isActive
                      ? "#f0f7ff"
                      : isDisabled
                      ? "#fafafa"
                      : "#fff",
                  }}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{item.Name}</Text>
                      </Space>
                    }
                    description={
                      <Text type="secondary" ellipsis>
                        {renderInlineBoldPreview(item.Content)}
                      </Text>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>

        {/* RIGHT PANEL – PREVIEW */}
        <div style={{ width: "55%" }}>
          <Card
            style={{
              height: "100%",
              borderRadius: 12,
              background: "#fafafa",
            }}
          >
            {selected ? (
              <>
                <Image
                  src="/img/image_placeholder_templates.png"
                  alt="Preview"
                  width="100%"
                  height={160}
                  style={{
                    objectFit: "cover",
                    borderRadius: 8,
                    marginBottom: 16,
                  }}
                />

                {/* <div
                  style={{
                    background: "#e6f4ff",
                    height: 160,
                    borderRadius: 8,
                    marginBottom: 16,
                  }}
                /> */}

                <div style={{ fontSize: 14 }}>
                  {" "}
                  {renderMessage(selected.Content)}
                </div>

                <Space
                  direction="vertical"
                  style={{ width: "100%", marginTop: 16 }}
                >
                  {selected.TextLink1 && (
                    <Button
                      block
                      style={{
                        color: "#00A884",
                        fontWeight: 600,
                      }}
                      icon={
                        <img
                          src="/icon/icon-button-whatsapp.svg"
                          style={{ width: 16 }}
                        />
                      }
                    >
                      {selected.TextLink1}
                    </Button>
                  )}

                  {selected.TextLink2 && (
                    <Button
                      block
                      style={{
                        color: "#00A884",
                        fontWeight: 600,
                      }}
                      icon={
                        <img
                          src="/icon/icon-button-whatsapp.svg"
                          style={{ width: 16 }}
                        />
                      }
                    >
                      {selected.TextLink2}{" "}
                    </Button>
                  )}
                </Space>
              </>
            ) : (
              <Text type="secondary">Select a template to preview</Text>
            )}
          </Card>
        </div>
      </div>

      {/* FOOTER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 16,
        }}
      >
        <Text>
          Selected: <Text strong>{selected?.Name || "-"}</Text>
        </Text>

        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            disabled={!selected}
            onClick={() => selected && onSelect(selected)}
          >
            Select Template
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default MessageTemplatesModal;
